import React, {useCallback, useMemo, useState} from 'react';
import {
  AuthSettings,
  ReducerState,
  SignInArgs,
  SignUpArgs,
} from '@taopix/taopix-core/dist/types/types';
import APIServer from '@taopix/taopix-core/dist/types/Service/APIServer';
import {
  LoginOptionsDialog,
  LoginDialog,
  SignUpDialog,
  ForgottenPasswordDialog,
} from '@taopix/taopix-design-system';
import {ControlCentreUser} from '../App/App';
import {useUserFlowState} from '../Context/UserFlow/UserFlowContext';
import {UserFlowStep} from '../../Facade/UserFlow/BaseUserFlow';
import {useTaopix} from '../Context/TaopixContext';
import {AppBusy} from '@taopix/taopix-core';
import {UserFlowType} from '../../Facade/UserFlow/UserFlowFacade';
import {ForgottenPasswordResponse} from '../../types';
import {LoginUserState} from '@taopix/taopix-design-system/dist/types/Components/Login/LoginDialog';
import useSettings from '../../Hooks/useSettings';
import {useExperience} from '../Experience/ExperienceProvider';

export type RegisterUserState = {
  firstname: string;
  lastname: string;
  username?: string;
  email: string;
  newpassword: string;
  marketing: boolean;
};

export type AppAuthProps = {
  server: APIServer;
  authentication: AuthSettings;
  initialState?: AuthenticationState | null;
  onClose?: () => void;
  isForceLogin?: boolean;
};

export enum AuthenticationState {
  Login = 'login',
  Register = 'register',
  ForgotPassword = 'forgotPassword',
}

const AppAuth = ({server, authentication, onClose, isForceLogin}: AppAuthProps) => {
  const [authError, setAuthError] = useState<Partial<Record<AuthenticationState, string>>>();
  const settings = useSettings();
  const userFlow = useUserFlowState();
  const {experience} = useExperience();
  const app = useTaopix();

  /**
   * Authenticates a user with the server
   *
   * @param state Data from the login form.
   * @returns Promise with server login response data.
   */
  const onLoginSubmit = (state: LoginUserState): Promise<ControlCentreUser> => {
    setAuthError({});

    return app.core
      .get('stateManager')
      .call<SignInArgs, ReducerState>('signIn', {
        login: state.login,
        password: state.password,
        rememberMe: state.rememberMe || false,
      })
      .then(response => {
        onAnyClose();
        const payload = response as ReducerState & ControlCentreUser;
        return {
          userId: payload.userId,
          userName: payload.userName,
          emailAddress: payload.emailAddress,
          login: payload.login,
          canCreateAccounts: payload.canCreateAccounts,
          isOrderSession: payload.isOrderSession,
          error: payload.error,
        };
      })
      .catch(response => {
        const error = server.getErrorFromResponse(response);
        setAuthError({
          [AuthenticationState.Login]: error,
        });
        throw error;
      });
  };

  /**
   * Call the server to register a new user.
   *
   * @param state Data from the registration form.
   * @returns Promise with server user response data.
   */
  const onRegisterSubmit = (state: RegisterUserState): Promise<ControlCentreUser> => {
    return app.core
      .get('stateManager')
      .call<SignUpArgs, ReducerState>('signUp', {
        firstName: state.firstname as string,
        lastName: state.lastname,
        login: state.username as string,
        password: state.newpassword,
        emailAddress: state.email,
        sendMarketingInfo: state.marketing,
      })
      .then(response => {
        onAnyClose();
        const payload = response as ReducerState & ControlCentreUser;
        return {
          userId: payload.userId,
          userName: payload.userName,
          emailAddress: payload.emailAddress,
          login: payload.login,
          canCreateAccounts: payload.canCreateAccounts,
          isOrderSession: payload.isOrderSession,
          error: payload.error,
        };
      })
      .catch(response => {
        const error = server.getErrorFromResponse(response);
        setAuthError({
          [AuthenticationState.Register]: error,
        });

        throw error;
      });
  };

  /**
   * Calls the server to initiate the forgotten password process.
   *
   * @param data Data from the forgotten password form.
   * @returns Promise with server response data.
   */
  const onForgotPasswordSubmit = (data: string): Promise<ForgottenPasswordResponse> => {
    return server
      .post<ForgottenPasswordResponse>(
        '/api/user/forgottenpassword',
        {
          login: data,
        },
        {json: true, token: 'token', blocking: AppBusy.AutoCancel}
      )
      .then(response => {
        return response.data;
      })
      .catch(response => {
        const error = server.getErrorFromResponse(response);
        setAuthError({
          [AuthenticationState.ForgotPassword]: error,
        });

        throw error;
      });
  };

  /**
   * Action to execute when a dialog is cancel.
   */
  const onCancel = () => {
    setAuthError({});

    userFlow.setCurrentStep(`${UserFlowType.Auth}.${UserFlowStep.None}`);
  };

  /**
   * Action to execute when any dialog is closed.
   * Usually through a positive action.
   */
  const onAnyClose = () => {
    setAuthError({});
    // if part of a userflow, then it will call the next step,
    // if now part of a userflow (opened from the app menu) it will close the dialog.
    userFlow.setNextStep();

    if (typeof onClose === 'function') {
      onClose();
    }
  };

  const isProjectStart = useMemo(
    () => UserFlowType.Start === userFlow.getStateStage(),
    [userFlow.getStateStage()]
  );

  const canSignIn = useMemo(() => {
    return Boolean(authentication?.canSignIn);
  }, [isProjectStart, authentication?.canSignIn]);

  const canRegister = useMemo(() => {
    const canCreateAccounts = Boolean(authentication?.canCreateAccounts);
    if (isProjectStart) {
      return canCreateAccounts && Boolean(experience?.SETTINGS?.guestAccess?.allowGuestsToRegister);
    }

    return canCreateAccounts;
  }, [
    isProjectStart,
    authentication?.canCreateAccounts,
    experience?.SETTINGS?.guestAccess?.allowGuestsToRegister,
  ]);

  if (!app.state.loaded) return <></>;

  return (
    <>
      <LoginOptionsDialog
        isOpen={userFlow.isStepActive(UserFlowType.Auth, UserFlowStep.SigninSelector)}
        id="login-options"
        onSelect={option => {
          userFlow.setCurrentStep(`${UserFlowType.Auth}.${option.toLowerCase()}`);
        }}
        allowSignIn={canSignIn}
        allowGuestAccess={experience.SETTINGS.guestAccess.allowGuestAccess}
        allowRegistration={canRegister}
        theme={'tpx-theme-dialogs'}
        themeTip={'Dialogs'}
      />

      <LoginDialog
        isOpen={userFlow.isStepActive(UserFlowType.Auth, UserFlowStep.Login)}
        onSelect={option => {
          userFlow.setCurrentStep(`${UserFlowType.Auth}.${option.toLowerCase()}`);
        }}
        allowGuestAccess={experience.SETTINGS.guestAccess.allowGuestAccess}
        allowRegistration={canRegister}
        onLogin={onLoginSubmit}
        onCancel={!isForceLogin ? onCancel : undefined}
        promptMessage={authError?.[AuthenticationState.Login]}
        login={authentication.login}
        theme={'tpx-theme-dialogs'}
        themeTip="Dialogs"
      />

      <SignUpDialog
        isOpen={userFlow.isStepActive(UserFlowType.Auth, UserFlowStep.Register)}
        onSelect={option => {
          userFlow.setCurrentStep(`${UserFlowType.Auth}.${option}`);
        }}
        identifier={settings.state.authentication.registerUsingEmail ? 'email' : 'username'}
        allowSignIn={canSignIn}
        onRegisterAccount={onRegisterSubmit}
        onCancel={onCancel}
        minPasswordStrength={settings.state.authentication.passwordStrengthMin}
        promptMessage={authError?.[AuthenticationState.Register]}
        termsAndConditionsURL={
          experience.SETTINGS.guestAccess.terms ? settings.state.urls.terms : ''
        }
        theme={'tpx-theme-dialogs'}
        themeTip="Dialogs"
      />

      <ForgottenPasswordDialog
        isOpen={userFlow.isStepActive(UserFlowType.Auth, UserFlowStep.ForgotPassword)}
        onClose={() => {
          userFlow.setCurrentStep(`${UserFlowType.Auth}.${UserFlowStep.Login}`);
        }}
        onForgotPassword={onForgotPasswordSubmit}
        onContinue={(event, redirectUrl: string) => {
          if ('' !== redirectUrl) {
            window.open(redirectUrl, '_blank');
          }

          userFlow.setCurrentStep(`${UserFlowType.Auth}.${UserFlowStep.Login}`);
        }}
        promptMessage={authError?.[AuthenticationState.ForgotPassword]}
        theme={'tpx-theme-dialogs'}
        themeTip="Dialogs"
      />
    </>
  );
};

export default AppAuth;
